import React, { useCallback, useMemo, useState } from 'react';

import {
  Alert,
  Box,
  Button,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from '@mui/material';

import { useUpdateBundlePricing } from '~/hooks/apis/accommodation/useBundles';

import { Bundle } from '~/services/AccommodationService';

import { EXAMPLE_GUESTS, EXAMPLE_NIGHTS } from './constants';

interface Props {
  bundle: Bundle;
}

export default function BundlePricingSection({ bundle }: Props) {
  const firstOffer = bundle.offers[0];
  const firstRule = bundle.rules[0];

  const [price, setPrice] = useState(firstOffer?.price ?? 0);
  const [currency, setCurrency] = useState(firstOffer?.currency ?? 'AUD');
  const [perNight, setPerNight] = useState(firstRule?.perNight ?? false);
  const [perOccupant, setPerOccupant] = useState(firstRule?.perOccupant ?? false);

  const updatePricingMutation = useUpdateBundlePricing();

  const exampleTotal = useMemo(() => {
    let total = price;
    if (perNight) {
      total *= EXAMPLE_NIGHTS;
    }
    if (perOccupant) {
      total *= EXAMPLE_GUESTS;
    }
    return total;
  }, [price, perNight, perOccupant]);

  const hasChanges =
    price !== (firstOffer?.price ?? 0) ||
    currency !== (firstOffer?.currency ?? 'AUD') ||
    perNight !== (firstRule?.perNight ?? false) ||
    perOccupant !== (firstRule?.perOccupant ?? false);

  const handleSave = useCallback(() => {
    updatePricingMutation.mutate({
      bundleId: bundle.id,
      payload: { price, currency, perOccupant, perNight },
    });
  }, [bundle.id, price, currency, perOccupant, perNight, updatePricingMutation]);

  if (bundle.offers.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No offers configured for this bundle. Pricing is set per offer.
      </Typography>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" spacing={3} alignItems="flex-start">
        <TextField
          label="Unit Sell Price"
          type="number"
          value={price}
          onChange={(e) => setPrice(Math.max(0, Number(e.target.value)))}
          inputProps={{ min: 0, step: 0.01 }}
          InputProps={{
            startAdornment: <InputAdornment position="start">{currency}</InputAdornment>,
          }}
          size="small"
          sx={{ width: 220 }}
        />
        <TextField
          label="Currency"
          value={currency}
          onChange={(e) => setCurrency(e.target.value.toUpperCase())}
          size="small"
          sx={{ width: 120 }}
          inputProps={{ maxLength: 3 }}
        />
      </Stack>

      <Stack direction="row" spacing={4}>
        <FormControl>
          <FormLabel>Unit</FormLabel>
          <RadioGroup
            value={perNight ? 'per_night' : 'per_stay'}
            onChange={(e) => setPerNight(e.target.value === 'per_night')}
          >
            <FormControlLabel value="per_stay" control={<Radio />} label="Per Stay" />
            <FormControlLabel value="per_night" control={<Radio />} label="Per Night" />
          </RadioGroup>
        </FormControl>

        <FormControl>
          <FormLabel>Occupancy</FormLabel>
          <RadioGroup
            value={perOccupant ? 'per_person' : 'per_accommodation'}
            onChange={(e) => setPerOccupant(e.target.value === 'per_person')}
          >
            <FormControlLabel value="per_accommodation" control={<Radio />} label="Per Accommodation" />
            <FormControlLabel value="per_person" control={<Radio />} label="Per Person" />
          </RadioGroup>
        </FormControl>
      </Stack>

      <Alert severity="info" variant="outlined">
        <Typography variant="body2">
          <strong>Example calculation:</strong> {currency} {price.toFixed(2)} ×{' '}
          {perNight ? `${EXAMPLE_NIGHTS} nights` : '1 stay'} ×{' '}
          {perOccupant ? `${EXAMPLE_GUESTS} persons` : '1 accommodation'} ={' '}
          <strong>
            {currency} {exampleTotal.toFixed(2)}
          </strong>
        </Typography>
      </Alert>

      <Box>
        <Button
          variant="contained"
          color="primary"
          onClick={handleSave}
          disabled={updatePricingMutation.isPending || !hasChanges}
        >
          {updatePricingMutation.isPending ? 'Saving...' : 'Save Pricing'}
        </Button>
      </Box>
    </Stack>
  );
}
